import { useState, useCallback, useRef } from 'react';
import { ResearchState, ResearchEvent } from '../types/research';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || window.location.origin;

const initialState: ResearchState = {
  isRunning: false,
  query: '',
  currentPhase: 'idle',
  searchPlan: [],
  searchResults: [],
  searchesCount: 0,
  searchesCompleted: 0,
  report: null,
  error: null,
  events: []
};

export const useResearchSSE = () => {
  const [state, setState] = useState<ResearchState>(initialState);
  const eventSourceRef = useRef<EventSource | null>(null);

  const handleEvent = useCallback((event: ResearchEvent) => {
    setState(prevState => {
      const newState = { ...prevState };
      newState.events = [...prevState.events, event];

      switch (event.type) {
        case 'research_started':
          newState.isRunning = true;
          newState.currentPhase = 'planning';
          newState.query = event.data.query;
          newState.error = null;
          newState.searchResults = []; // Reset search results
          break;

        case 'planning_started':
          newState.currentPhase = 'planning';
          break;

        case 'planning_completed':
          newState.searchPlan = event.data.searches;
          newState.searchesCount = event.data.searches_count;
          newState.currentPhase = 'searching';
          break;

        case 'searching_started':
          newState.currentPhase = 'searching';
          break;

        case 'search_completed':
          // Add the completed search result
          const searchResult = {
            search_index: event.data.search_index,
            search_query: event.data.search_query,
            search_reason: event.data.search_reason,
            result_summary: event.data.result_summary,
            timestamp: event.timestamp
          };
          newState.searchResults = [...prevState.searchResults, searchResult];
          break;

        case 'search_progress':
          newState.searchesCompleted = event.data.completed;
          break;

        case 'searching_completed':
          newState.currentPhase = 'writing';
          break;

        case 'writing_started':
          newState.currentPhase = 'writing';
          break;

        case 'writing_progress':
          // Keep the writing phase active and optionally update progress info
          newState.currentPhase = 'writing';
          // You could add writing progress details to state if needed
          break;

        case 'research_completed':
          newState.isRunning = false;
          newState.currentPhase = 'completed';
          newState.report = event.data;
          break;

        case 'error':
          newState.isRunning = false;
          newState.currentPhase = 'error';
          newState.error = event.data.message;
          break;

        case 'stream_end':
          // Stream has ended, ensure we're not running anymore
          // But preserve the current phase if it's already 'completed' or 'error'
          newState.isRunning = false;
          if (newState.currentPhase !== 'completed' && newState.currentPhase !== 'error') {
            // If we get stream_end without research_completed, something went wrong
            newState.currentPhase = 'error';
            newState.error = 'Research stream ended unexpectedly';
          }
          break;
      }

      return newState;
    });
  }, []);

  const startResearch = useCallback(async (query: string) => {
    // Check current state to prevent multiple concurrent requests
    setState(currentState => {
      if (currentState.isRunning) {
        console.log('⚠️ Research already running, ignoring new request');
        return currentState; // Don't start if already running
      }
      // Reset state and start research
      console.log('🚀 Starting new research for:', query);
      return {
        ...initialState,
        isRunning: true,
        query: query,
        currentPhase: 'planning'
      };
    });

    try {
      const response = await fetch(`${API_BASE_URL}/research/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No reader available');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let currentEventType = '';
      let shouldClose = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done || shouldClose) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEventType = line.substring(7);
          } else if (line.startsWith('data: ') && currentEventType) {
            try {
              const data = JSON.parse(line.substring(6));
              const event: ResearchEvent = {
                type: currentEventType as any,
                data,
                timestamp: new Date().toISOString()
              };
              handleEvent(event);
              
              // Close connection after stream_end event
              if (currentEventType === 'stream_end') {
                shouldClose = true;
              }
              
              currentEventType = ''; // Reset after processing
            } catch (e) {
              console.error('Failed to parse SSE data:', e);
            }
          }
        }
      }
      
      // Ensure reader is properly closed
      await reader.cancel();
      
      // Final cleanup - ensure research is marked as not running
      setState(prevState => ({
        ...prevState,
        isRunning: false
      }));
    } catch (error) {
      console.error('Research failed:', error);
      setState(prevState => ({
        ...prevState,
        isRunning: false,
        currentPhase: 'error',
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      }));
    }
  }, [handleEvent]);

  const resetResearch = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setState(initialState);
  }, []);

  return {
    state,
    startResearch,
    resetResearch
  };
}; 